import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface IUseOverflowDetectionResult {
  hasOverflow: boolean;
}

function useOverflowDetection(ref: RefObject<HTMLElement | null>): IUseOverflowDetectionResult {
  const [hasOverflow, setHasOverflow] = useState(false);
  const observersRef = useRef<{ resize: ResizeObserver; mutation: MutationObserver } | null>(null);
  const observedElementRef = useRef<HTMLElement | null>(null);

  const checkOverflowOnElement = useCallback((node: HTMLElement | null) => {
    if (!node?.isConnected) {
      setHasOverflow(false);
      return;
    }
    setHasOverflow(node.scrollHeight > node.clientHeight);
  }, []);

  const setupObservers = useCallback(
    (node: HTMLElement) => {
      if (observersRef.current) {
        observersRef.current.resize.disconnect();
        observersRef.current.mutation.disconnect();
        observersRef.current = null;
      }

      if (!node.isConnected) {
        observedElementRef.current = null;
        return;
      }

      observedElementRef.current = node;

      const resizeObserver = new ResizeObserver(() => {
        checkOverflowOnElement(observedElementRef.current);
      });

      const mutationObserver = new MutationObserver(() => {
        checkOverflowOnElement(observedElementRef.current);
      });

      resizeObserver.observe(node);
      mutationObserver.observe(node, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });

      observersRef.current = { resize: resizeObserver, mutation: mutationObserver };

      checkOverflowOnElement(node);
    },
    [checkOverflowOnElement],
  );

  useEffect(() => {
    const initialElement = ref.current;
    if (initialElement?.isConnected) {
      setupObservers(initialElement);
    }

    let animationFrameId: number | null = null;
    const checkForRefChange = (): void => {
      const currentElement = ref.current;
      const observedElement = observedElementRef.current;

      if (observedElement && !observedElement.isConnected) {
        if (observersRef.current) {
          observersRef.current.resize.disconnect();
          observersRef.current.mutation.disconnect();
          observersRef.current = null;
        }
        observedElementRef.current = null;
        setHasOverflow(false);
      }

      if (currentElement?.isConnected && currentElement !== observedElementRef.current) {
        setupObservers(currentElement);
      }

      animationFrameId = requestAnimationFrame(checkForRefChange);
    };

    animationFrameId = requestAnimationFrame(checkForRefChange);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      if (observersRef.current) {
        observersRef.current.resize.disconnect();
        observersRef.current.mutation.disconnect();
        observersRef.current = null;
      }
      observedElementRef.current = null;
    };
  }, [ref, setupObservers]);

  return { hasOverflow };
}

export default useOverflowDetection;
